import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useWorkflow } from '../context/WorkflowContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { fetchModels } from '../services/osintService';
import SearchableModelSelect from './SearchableModelSelect';

const {
  FiCheck, FiRefreshCw, FiShield, FiZap, 
  FiGlobe, FiTrash2, FiKey, FiCpu, FiSun, FiMoon, FiMonitor, FiActivity
} = FiIcons;

const STATUS_STYLES = {
  idle: { ring: 'border-sonar-border', dot: 'bg-slate-600', label: 'Disconnected', text: 'text-sonar-text-muted' },
  loading: { ring: 'border-amber-500/30', dot: 'bg-amber-400 animate-pulse', label: 'Connecting...', text: 'text-amber-400' },
  success: { ring: 'border-sonar-green/50', dot: 'bg-sonar-green', label: 'Linked', text: 'text-sonar-green' },
  error: { ring: 'border-red-500/30', dot: 'bg-red-500', label: 'Invalid Key', text: 'text-red-400' },
};

const SettingsPage = () => {
  const {
    isDark, setIsDark, apiKey, setApiKey, provider, setProvider,
    selectedModel, setSelectedModel, openRouterKey, setOpenRouterKey,
    openaiKey, setOpenaiKey, anthropicKey, setAnthropicKey,
    geminiKey, setGeminiKey, deepseekKey, setDeepseekKey, qwenKey, setQwenKey
  } = useWorkflow();

  const [statuses, setStatuses] = useState({
    straico: apiKey ? 'success' : 'idle',
    openrouter: openRouterKey ? 'success' : 'idle',
    openai: openaiKey ? 'success' : 'idle',
    anthropic: anthropicKey ? 'success' : 'idle',
    gemini: geminiKey ? 'success' : 'idle',
    deepseek: deepseekKey ? 'success' : 'idle',
    qwen: qwenKey ? 'success' : 'idle',
  });

  const [models, setModels] = useState({ 
    straico: [], openrouter: [], openai: [], anthropic: [], 
    gemini: [], deepseek: [], qwen: [] 
  });

  useEffect(() => {
    if (apiKey) verifyAI('straico', apiKey);
    if (openRouterKey) verifyAI('openrouter', openRouterKey);
    if (geminiKey) verifyAI('gemini', geminiKey);
    if (deepseekKey) verifyAI('deepseek', deepseekKey);
    if (qwenKey) verifyAI('qwen', qwenKey);
  }, []);

  const verifyAI = async (id, key) => {
    if (!key) return;
    setStatuses(p => ({ ...p, [id]: 'loading' }));
    try {
      const result = await fetchModels(key, id);
      setModels(p => ({ ...p, [id]: result }));
      setStatuses(p => ({ ...p, [id]: 'success' }));
    } catch (err) {
      setStatuses(p => ({ ...p, [id]: 'error' }));
    }
  };

  const configs = [
    { id: 'straico', name: 'Straico', icon: <SafeIcon icon={FiZap} />, description: 'Neural Aggregator' },
    { id: 'openrouter', name: 'OpenRouter', icon: <SafeIcon icon={FiGlobe} />, description: 'Universal LLM Proxy' },
    { id: 'gemini', name: 'Google Gemini', icon: <SafeIcon icon={FiActivity} />, description: 'Multimodal Intelligence' },
    { id: 'deepseek', name: 'DeepSeek', icon: <SafeIcon icon={FiCpu} />, description: 'Reasoning Optimized' },
    { id: 'qwen', name: 'Alibaba Qwen', icon: <SafeIcon icon={FiShield} />, description: 'Enterprise Grade LLM' },
    { id: 'openai', name: 'OpenAI', icon: <SafeIcon icon={FiCpu} />, description: 'GPT-4o Native' },
    { id: 'anthropic', name: 'Anthropic', icon: <SafeIcon icon={FiShield} />, description: 'Claude 3.5 Native' },
  ];

  const keyStore = {
    straico: { get: () => apiKey, set: setApiKey },
    openrouter: { get: () => openRouterKey, set: setOpenRouterKey },
    gemini: { get: () => geminiKey, set: setGeminiKey },
    deepseek: { get: () => deepseekKey, set: setDeepseekKey },
    qwen: { get: () => qwenKey, set: setQwenKey },
    openai: { get: () => openaiKey, set: setOpenaiKey },
    anthropic: { get: () => anthropicKey, set: setAnthropicKey },
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <div className="mb-12">
        <h1 className="text-4xl font-black text-sonar-text uppercase tracking-tight mb-3">Operations <span className="text-sonar-green">Hub</span></h1>
        <p className="text-sonar-text-muted font-mono text-sm uppercase tracking-widest">Neural Linkages & Engine Configuration</p>
      </div>

      <section className="mb-12">
        <h2 className="text-xs font-black text-sonar-text uppercase tracking-[0.3em] mb-6 flex items-center gap-3">
          <div className="w-1.5 h-1.5 rounded-full bg-sonar-green" /> Interface Matrix
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-sonar-card border border-sonar-border p-6 rounded-3xl">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-sonar-text mb-1">Visual Protocol</h3>
                <p className="text-[10px] text-sonar-text-muted font-mono uppercase tracking-widest">{isDark ? 'Dark Mode' : 'Light Mode'}</p>
              </div>
              <button onClick={() => setIsDark(!isDark)} className="w-14 h-8 bg-sonar-bg border border-sonar-border rounded-full relative transition-all">
                <div className={`absolute top-1 w-6 h-6 rounded-full flex items-center justify-center transition-all ${isDark ? 'right-1 bg-sonar-green text-black' : 'left-1 bg-sonar-text-muted text-white'}`}>
                  <SafeIcon icon={isDark ? FiMoon : FiSun} className="text-xs" />
                </div>
              </button>
            </div>
          </div>
          
          <div className="bg-sonar-card border border-sonar-border p-6 rounded-3xl">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-sonar-text mb-1">Active Engine</h3>
                <p className="text-[10px] text-sonar-text-muted font-mono uppercase tracking-widest">{provider.toUpperCase()}</p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-sonar-green/10 border border-sonar-green/30 flex items-center justify-center text-sonar-green">
                <SafeIcon icon={FiMonitor} />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-xs font-black text-sonar-text uppercase tracking-[0.3em] mb-6 flex items-center gap-3">
          <div className="w-1.5 h-1.5 rounded-full bg-sonar-green" /> Neural Linkages
        </h2>
        <div className="space-y-4">
          {configs.map(config => {
            const st = STATUS_STYLES[statuses[config.id]] || STATUS_STYLES.idle;
            const storedKey = keyStore[config.id].get();
            const [tempKey, setTempKey] = useState(storedKey || '');
            const [isExpanded, setIsExpanded] = useState(!storedKey);

            return (
              <div key={config.id} className={`bg-sonar-card border rounded-3xl transition-all duration-500 overflow-hidden ${provider === config.id ? 'border-sonar-green shadow-[0_0_30px_rgba(46,204,113,0.1)]' : 'border-sonar-border'}`}>
                <div className="flex items-center justify-between p-5 cursor-pointer hover:bg-sonar-green/5 transition-colors" onClick={() => setIsExpanded(!isExpanded)}>
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-xl border transition-all ${statuses[config.id] === 'success' ? 'bg-sonar-green/10 border-sonar-green/30 text-sonar-green' : 'bg-sonar-bg border-sonar-border text-sonar-text-muted'}`}>
                      {config.icon}
                    </div>
                    <div>
                      <h3 className="font-bold text-sonar-text text-sm uppercase tracking-widest">{config.name}</h3>
                      <p className="text-[10px] text-sonar-text-muted font-mono mt-0.5">{config.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                       <div className={`w-2 h-2 rounded-full ${st.dot}`} />
                       <span className={`text-[10px] font-black uppercase tracking-widest ${st.text}`}>{st.label}</span>
                    </div>
                    {provider !== config.id && statuses[config.id] === 'success' && (
                      <button onClick={(e) => { e.stopPropagation(); setProvider(config.id); setApiKey(storedKey); }} className="px-3 py-1.5 bg-sonar-bg border border-sonar-border rounded-lg text-[9px] font-black text-sonar-text-muted hover:border-sonar-green hover:text-sonar-green transition-all uppercase tracking-widest">Activate</button>
                    )}
                  </div>
                </div>

                <AnimatePresence>
                  {isExpanded && (
                    <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="px-6 pb-6 pt-2 border-t border-sonar-border bg-sonar-bg/20">
                      <div className="space-y-6 pt-4">
                        <div>
                          <label className="block text-[10px] font-bold text-sonar-text-muted uppercase tracking-widest mb-3">API Credential</label>
                          <div className="flex gap-3">
                            <div className="relative flex-1">
                              <SafeIcon icon={FiKey} className="absolute left-4 top-1/2 -translate-y-1/2 text-sonar-green/40" />
                              <input type="password" value={tempKey} onChange={(e) => setTempKey(e.target.value)} className="w-full pl-12 pr-4 py-3.5 bg-sonar-bg border border-sonar-border rounded-2xl text-sonar-text font-mono text-xs outline-none focus:border-sonar-green transition-all" placeholder={`Enter ${config.name} Key...`} />
                            </div>
                            <button onClick={() => { keyStore[config.id].set(tempKey); verifyAI(config.id, tempKey); if(provider === config.id) setApiKey(tempKey); }} disabled={!tempKey || statuses[config.id] === 'loading'} className="px-6 bg-sonar-green text-black rounded-2xl font-black uppercase tracking-widest text-[10px] disabled:opacity-30">
                              {statuses[config.id] === 'loading' ? <SafeIcon icon={FiRefreshCw} className="animate-spin" /> : 'Link'}
                            </button>
                            {storedKey && (
                              <button onClick={() => { keyStore[config.id].set(''); setStatuses(p => ({ ...p, [config.id]: 'idle' })); }} className="p-3.5 bg-red-500/10 border border-red-500/20 text-red-400 rounded-2xl"><SafeIcon icon={FiTrash2} /></button>
                            )}
                          </div>
                        </div>

                        {statuses[config.id] === 'success' && (
                          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                            <label className="block text-[10px] font-bold text-sonar-text-muted uppercase tracking-widest mb-3">Model Selection & Pricing (1M Tokens)</label>
                            <SearchableModelSelect models={models[config.id]} value={selectedModel} onChange={setSelectedModel} />
                          </motion.div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default SettingsPage;